// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { BrandModel, CategoryModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { xSeoHandlerType } from "@vanmeter/App/Handlers/XNG/Seo";
import { IEventModel } from "@vanmeter/App/Services/EventListService";
import { FeaturedBrandsModule, IBlogModel, ImageModel, VideoModel } from "@vanmeter/XDL/types";
import { Draft } from "immer";

export interface BaseWidgetSeoModel<T> {
    name: string;
    id: string;
    isFeatured?: boolean;
    type?: string;
    items: T[];
}

export interface IndustrySeoItemModel {
    industryName: string;
    title?: string;
    description?: string;
    url?: string;
    cta1?: { label: string; url: string };
    cta2?: { label: string; url: string };
}

export interface xSeoSchemaModel<T> {
    isActive?: boolean;
    widgets: BaseWidgetSeoModel<T>[];
}

export type xSeoStateType = Readonly<{
    categories?: xSeoSchemaModel<CategoryModel>;
    brands?: xSeoSchemaModel<FeaturedBrandsModule>;
    productCarousels?: xSeoSchemaModel<ProductModel>;
    images: xSeoSchemaModel<ImageModel>;
    videos: xSeoSchemaModel<VideoModel>;
    blogs: xSeoSchemaModel<IBlogModel>;
    events: xSeoSchemaModel<IEventModel>;
    industries?: xSeoSchemaModel<IndustrySeoItemModel>;
}>;

export const initialxSeo: xSeoStateType = {
    categories: {
        widgets: [],
    },
    brands: {
        widgets: [],
    },
    productCarousels: {
        widgets: [],
    },
    images: {
        widgets: [],
    },
    videos: {
        widgets: [],
    },
    blogs: {
        widgets: [],
    },
    events: {
        widgets: [],
    },
    industries: {
        widgets: [],
    },
};

const reducer = {
    "xSeo/update": (draft: Draft<xSeoStateType>, action: { data: xSeoHandlerType }) => {
        const { key, operation, value } = action.data;
        const entity = draft[key as keyof xSeoStateType] as xSeoSchemaModel<any>;

        if (!entity) {
            return;
        }

        switch (operation) {
            case "add":
                entity.widgets.push(value);
                entity.isActive = true;
                break;

            case "delete": {
                entity.widgets = entity.widgets.filter((item: any) => item.id !== value.id);
                entity.isActive = entity.widgets.length > 0;
                break;
            }

            case "replace": {
                entity.widgets = entity.widgets.filter((item: any) => item.id !== value.id);
                entity.widgets.push(value);
                entity.isActive = entity.widgets.length > 0;
                break;
            }

            default:
                entity.widgets.push(value);
                entity.isActive = true;
        }
    },
};

const xSeo = createTypedReducerWithImmer(initialxSeo, reducer);

(reducers as any).xSeo = xSeo;

export type xSeoState = {
    xSeo: xSeoStateType;
} & ApplicationState;

type xSeoReducers = {
    [key: string]: typeof xSeo;
};

export type xSeoActions = Parameters<xSeoReducers[keyof xSeoReducers]>[1] | AnyAction;
